<template>
  <div>
    <BaseCircularLoadingBar v-if="pending" :size="50" :text="$t('page.opportunity.loading-document-text')" />
    <div v-else>
      <div class="opportunity-documents">
        <div v-if="opportunityDocuments.length > 0">
          <div class="opportunity-documents__documents">
            <BaseDocumentTag
              icon="file-text"
              v-for="document in opportunityDocuments"
              :key="document.id"
              :document="document"
              @click.native.stop
              @document-selected="goToOpportunityDocumentAnalysis"
              @download-document="downloadDocument"
              @delete-document="deleteOpportunityDocument"
              actionable
              show-delete
            />
          </div>
          <FileUpload minimalist multiple icon="file-magnify" @add-files="addOpportunityDocuments" />
        </div>
        <FileUpload v-else multiple icon="file-magnify" @add-files="addOpportunityDocuments" />
      </div>
    </div>
    <DocumentDeleteConfirmationPopup
      ref="confirmationDialog"
      :opportunity-id="opportunity && opportunity.id"
      :manipulated-object="manipulatedObject"
      @on-confirm="executeDeleteDocument"
    />
  </div>
</template>

<script>
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseDocumentTag from '@/components/base/BaseDocumentTag';
import FileUpload from '@/components/form/FileUpload';

import { tenantHasSlipExtractionAccess, tenantHasLLMExtractionsAccess } from '@/util/module-access-util';
import { isPdfFile, isXlsFile } from '@/util/document-file-util';
import { mapActions, mapGetters } from 'vuex';
import { opportunityDocumentsService } from '@/services/opportunity-documents.service';
import DocumentDeleteConfirmationPopup from '@/components/page-opportunity-details/DocumentDeleteConfirmationPopup.vue';

export default {
  name: 'OpportunityDocuments',
  components: {
    DocumentDeleteConfirmationPopup,
    BaseCircularLoadingBar,
    BaseDocumentTag,
    FileUpload,
  },
  props: {
    opportunity: Object,
  },
  data() {
    return {
      manipulatedObject: null,
    };
  },
  computed: {
    ...mapGetters('opportunityDocuments', ['status']),
    pending() {
      if (!this.opportunity) {
        return false;
      }
      const oppStatus = this.status[this.opportunity.id];
      return oppStatus ? oppStatus.pending : false;
    },
    opportunityDocuments() {
      return this.opportunity ? this.opportunity.documents : [];
    },
  },
  methods: {
    ...mapActions('opportunityDocuments', ['addDocuments', 'deleteDocument']),
    tenantHasSlipExtractionAccess,
    tenantHasLLMExtractionsAccess,
    isXlsFile,
    isPdfFile,
    goToOpportunityDocumentAnalysis(document) {
      if (!document) return;
      if (!isPdfFile(document)) {
        this.$toast.warning(this.$t('page.opportunity.access.unavailable-document-visualization'));
        return;
      }

      this.$router.push({
        name: 'opportunity-analysis',
        params: { opportunityId: this.opportunity.id, documentId: document.id },
      });
    },
    addOpportunityDocuments(files) {
      const filesArray = Array.from(files);
      const containsForbiddenFormat = filesArray.some(
        (file) => (isXlsFile(file) && !tenantHasSlipExtractionAccess()) || !isPdfFile(file),
      );

      if (!containsForbiddenFormat) this.uploadDocumentsToServer(filesArray);
      else this.$toast.warning(this.$t('page.opportunity-download.wrong-format'));
    },
    downloadDocument(doc) {
      opportunityDocumentsService
        .getDocumentFile({ opportunityId: doc.opportunityId, documentId: doc.id })
        .then((fileBlob) => {
          const fileURL = window.URL.createObjectURL(fileBlob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = doc.name;
          link.click();
          link.remove();
          window.URL.revokeObjectURL(fileURL);
        });
    },
    deleteOpportunityDocument(document) {
      this.manipulatedObject = document;
      this.$refs.confirmationDialog.openDialog(document);
    },
    executeDeleteDocument() {
      this.deleteDocument({
        opportunity: this.opportunity,
        documentId: this.manipulatedObject.id,
      })
        .then(() => {
          this.$toast.success(this.$t('page.opportunity-details.document-manipulations.delete-success'));
          this.manipulatedObject = null;
          this.$emit('document-deleted');
        })
        .catch((error) => {
          this.$toast.error(this.$t('page.opportunity-details.document-manipulations.delete-fail', { file: error }));
          this.manipulatedObject = null;
        });
    },
    uploadDocumentsToServer(files) {
      let formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        let file = files[i];
        if (file) formData.append('file[]', file);
      }

      this.addDocuments({
        opportunity: this.opportunity,
        formData,
      })
        .then(() => {
          this.$toast.success(this.$t('page.opportunity-details.document-manipulations.add-success'));
          if (this.tenantHasLLMExtractionsAccess()) {
            this.$toast.success(this.$t('page.opportunity-details.document-manipulations.llm-extraction-scheduled'));
          }
          this.$emit('documents-uploaded');
        })
        .catch((error) => {
          this.$toast.error(
            error,
            {},
            this.$t('page.opportunity-details.document-manipulations.add-fail', { file: error.message }),
          );
        });
    },
  },
};
</script>

<style lang="scss">
.opportunity-documents {
  &__documents {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }
}
</style>
